import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertCircle, Pencil, TrendingUp, TrendingDown } from "lucide-react";
import { format, parseISO } from "date-fns";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";

const RUPEE_ICON = "/icons/cost-rupee.png";

const lastMondayMidnight = () => {
    const now = new Date();
    const daysSinceMon = (now.getDay() + 6) % 7; // Mon=0 ... Sun=6
    const d = new Date(now);
    d.setDate(now.getDate() - daysSinceMon);
    d.setHours(0, 0, 0, 0);
    return d;
};

const compactNumber = (n) => {
    if (n == null || Number.isNaN(n)) return "0";
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return (n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1) + "m";
    if (abs >= 1_000) return (n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1) + "k";
    return String(Math.round(n));
};

const formatInr = (n) =>
    new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(n || 0));

const Sparkline = ({ points }) => {
    if (!points || points.length < 2) return null;
    const w = 56;
    const h = 18;
    const pad = 2;
    const vals = points.map((p) => p.credits);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = max - min || 1;
    const step = (w - pad * 2) / (points.length - 1);
    const coords = points.map((p, i) => {
        const x = pad + i * step;
        const y = pad + (h - pad * 2) * (1 - (p.credits - min) / range);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    const last = coords[coords.length - 1].split(",");
    return (
        <svg width={w} height={h} className="shrink-0">
            <polyline
                fill="none"
                stroke="#2EB4AC"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                points={coords.join(" ")}
            />
            <circle cx={last[0]} cy={last[1]} r="2" fill="#2EB4AC" />
        </svg>
    );
};

const CostTrackerWidget = () => {
    const [state, setState] = useState(null);
    const [history, setHistory] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [creditsInput, setCreditsInput] = useState("");
    const [rateInput, setRateInput] = useState("");
    const pillRef = useRef(null);

    const fetchAll = async () => {
        try {
            const [s, h] = await Promise.all([
                api.get("/cost-tracker"),
                api.get("/cost-tracker/history?weeks=12"),
            ]);
            setState(s.data);
            setHistory(h.data?.history || []);
        } catch (e) {
            // silent — widget is discreet
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    // Collapse when clicking outside the pill
    useEffect(() => {
        if (!expanded) return;
        const onDown = (e) => {
            if (pillRef.current && !pillRef.current.contains(e.target)) {
                setExpanded(false);
            }
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [expanded]);

    const stale = useMemo(() => {
        if (!state?.updated_at) return true;
        return new Date(state.updated_at) < lastMondayMidnight();
    }, [state]);

    const totalCredits = Number(state?.total_credits || 0);
    const rate = Number(state?.cost_per_credit_inr || 13.7);
    const totalSpent = totalCredits * rate;

    const openDialog = () => {
        setCreditsInput(totalCredits ? String(totalCredits) : "");
        setRateInput(rate ? String(rate) : "");
        setDialogOpen(true);
        setExpanded(false);
    };

    const handleSave = async () => {
        const credits = creditsInput === "" ? null : Number(creditsInput);
        const perCredit = rateInput === "" ? null : Number(rateInput);
        if (credits == null && perCredit == null) {
            toast.error("Enter credits or per-credit rate");
            return;
        }
        if (credits != null && (Number.isNaN(credits) || credits < 0)) {
            toast.error("Credits must be a non-negative number");
            return;
        }
        if (perCredit != null && (Number.isNaN(perCredit) || perCredit < 0)) {
            toast.error("Rate must be a non-negative number");
            return;
        }
        setSaving(true);
        try {
            const payload = {};
            if (credits != null) payload.total_credits = credits;
            if (perCredit != null) payload.cost_per_credit_inr = perCredit;
            await api.put("/cost-tracker", payload);
            await fetchAll();
            toast.success("Cost tracker updated");
            setDialogOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update");
        } finally {
            setSaving(false);
        }
    };

    // Live preview for the dialog
    const previewTotal = useMemo(() => {
        const c = creditsInput === "" ? totalCredits : Number(creditsInput);
        const r = rateInput === "" ? rate : Number(rateInput);
        if (Number.isNaN(c) || Number.isNaN(r)) return 0;
        return c * r;
    }, [creditsInput, rateInput, totalCredits, rate]);

    // Trend delta (latest vs previous week)
    const trend = useMemo(() => {
        if (!history || history.length < 2) return null;
        const a = history[history.length - 2].credits;
        const b = history[history.length - 1].credits;
        const diff = b - a;
        const pct = a === 0 ? (b > 0 ? 100 : 0) : (diff / a) * 100;
        return { diff, pct, up: diff > 0, down: diff < 0 };
    }, [history]);

    const chartData = useMemo(
        () =>
            (history || []).map((h) => ({
                week: h.week_start,
                credits: h.credits,
            })),
        [history]
    );

    const updatedLabel = useMemo(() => {
        if (!state?.updated_at) return null;
        try {
            return format(parseISO(state.updated_at), "PPp");
        } catch {
            return null;
        }
    }, [state]);

    const updatedByLocal = (state?.updated_by || "").split("@")[0];

    return (
        <>
            <div
                ref={pillRef}
                className="relative inline-flex items-center"
                data-testid="cost-tracker-widget"
            >
                {!expanded ? (
                    <button
                        type="button"
                        onClick={() => setExpanded(true)}
                        aria-label="Open cost tracker"
                        data-testid="cost-tracker-icon-button"
                        className="relative h-9 w-9 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors"
                    >
                        <img
                            src={RUPEE_ICON}
                            alt=""
                            className="h-7 w-7 hover:scale-110 transition-transform"
                        />
                        {stale && (
                            <span
                                className="absolute top-0.5 right-0.5 flex h-2.5 w-2.5"
                                data-testid="cost-tracker-stale-dot"
                            >
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 ring-2 ring-white" />
                            </span>
                        )}
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={openDialog}
                        data-testid="cost-tracker-pill"
                        className="group relative h-11 rounded-xl border border-slate-200 bg-white shadow-sm flex items-stretch overflow-hidden animate-in fade-in slide-in-from-right-2 duration-200 hover:border-slate-300 transition-colors"
                    >
                        <div className="flex items-center gap-2 px-3 bg-slate-50/50">
                            <img src={RUPEE_ICON} alt="" className="h-4 w-4" />
                            <div className="leading-tight text-left">
                                <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                                    Credits
                                </div>
                                <div className="text-sm font-bold tabular-nums text-slate-900">
                                    {compactNumber(totalCredits)}
                                </div>
                            </div>
                        </div>
                        <div className="w-px bg-slate-200" />
                        <div className="flex items-center gap-2 px-3">
                            <div className="leading-tight text-left">
                                <div className="text-[9px] font-semibold uppercase tracking-wider text-[#2EB4AC]">
                                    Spent
                                </div>
                                <div className="text-sm font-bold tabular-nums text-slate-900">
                                    ₹{formatInr(totalSpent)}
                                </div>
                            </div>
                            <Sparkline points={history} />
                            {stale ? (
                                <span
                                    className="relative flex h-2 w-2"
                                    aria-label="Update needed"
                                >
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                                </span>
                            ) : (
                                <Pencil className="h-3.5 w-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                            )}
                        </div>
                    </button>
                )}
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg" data-testid="cost-tracker-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 font-heading">
                            <img src={RUPEE_ICON} alt="" className="h-5 w-5" />
                            App Development Cost Tracker
                        </DialogTitle>
                    </DialogHeader>

                    {stale && (
                        <div
                            className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900"
                            data-testid="cost-tracker-stale-alert"
                        >
                            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                            <span>
                                It&apos;s a new week — please update the credits consumed since last Monday.
                            </span>
                        </div>
                    )}

                    {chartData.length >= 2 ? (
                        <div className="rounded-lg border border-slate-200 bg-white p-3">
                            <div className="flex items-start justify-between mb-2">
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                                        Week-on-week Credits
                                    </div>
                                    <div className="text-[11px] text-slate-400 mt-0.5">
                                        Last {chartData.length} weeks · Mondays
                                    </div>
                                </div>
                                {trend && (trend.up || trend.down) && (
                                    <div
                                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                            trend.up
                                                ? "bg-red-50 text-red-600"
                                                : "bg-green-50 text-green-600"
                                        }`}
                                        data-testid="cost-tracker-trend-chip"
                                    >
                                        {trend.up ? (
                                            <TrendingUp className="h-3 w-3" />
                                        ) : (
                                            <TrendingDown className="h-3 w-3" />
                                        )}
                                        {trend.diff > 0 ? "+" : ""}
                                        {Math.round(trend.diff)} cr ({trend.pct > 0 ? "+" : ""}
                                        {Math.round(trend.pct)}%)
                                    </div>
                                )}
                            </div>
                            <div style={{ height: 176 }}>
                                <ResponsiveContainer>
                                    <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="costTrackerFill" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#2EB4AC" stopOpacity={0.35} />
                                                <stop offset="100%" stopColor="#2EB4AC" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                        <XAxis
                                            dataKey="week"
                                            tick={{ fontSize: 10, fill: "#64748b" }}
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={(v) => {
                                                try {
                                                    return format(parseISO(v), "d MMM");
                                                } catch {
                                                    return v;
                                                }
                                            }}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 10, fill: "#64748b" }}
                                            tickLine={false}
                                            axisLine={false}
                                            width={36}
                                            tickFormatter={(v) => compactNumber(v)}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                borderRadius: 12,
                                                border: "1px solid #e2e8f0",
                                                fontSize: 12,
                                            }}
                                            labelFormatter={(v) => {
                                                try {
                                                    return format(parseISO(v), "d MMM yyyy");
                                                } catch {
                                                    return v;
                                                }
                                            }}
                                            formatter={(v) => [compactNumber(v), "Credits"]}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="credits"
                                            stroke="#2EB4AC"
                                            strokeWidth={2}
                                            fill="url(#costTrackerFill)"
                                            dot={{ r: 3, fill: "#ffffff", stroke: "#2EB4AC", strokeWidth: 2 }}
                                            activeDot={{ r: 4 }}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/50 px-4 py-6 text-center text-sm text-slate-500">
                            Save updates each week to build the trendline.
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                Total credits consumed
                            </label>
                            <Input
                                type="number"
                                inputMode="decimal"
                                min="0"
                                step="1"
                                placeholder="0"
                                value={creditsInput}
                                onChange={(e) => setCreditsInput(e.target.value)}
                                className="mt-1"
                                data-testid="cost-tracker-credits-input"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                Per-credit cost (₹)
                            </label>
                            <Input
                                type="number"
                                inputMode="decimal"
                                min="0"
                                step="0.01"
                                placeholder="13.70"
                                value={rateInput}
                                onChange={(e) => setRateInput(e.target.value)}
                                className="mt-1"
                                data-testid="cost-tracker-rate-input"
                            />
                            <div className="text-[11px] text-slate-400 mt-1">
                                Default ₹13.70 — edit anytime as billing changes.
                            </div>
                        </div>
                    </div>

                    <div className="rounded-lg bg-slate-50/60 border border-slate-200 px-4 py-3 flex items-center justify-between">
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                                Estimated total
                            </div>
                            <div className="text-[11px] text-slate-400">
                                credits × per-credit rate
                            </div>
                        </div>
                        <div
                            className="text-2xl font-heading font-bold text-slate-900 tabular-nums"
                            data-testid="cost-tracker-preview-total"
                        >
                            ₹{formatInr(previewTotal)}
                        </div>
                    </div>

                    {updatedLabel && (
                        <div className="text-[11px] text-slate-400">
                            Last updated {updatedLabel}
                            {updatedByLocal ? ` by ${updatedByLocal}` : ""}
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            variant="ghost"
                            onClick={() => setDialogOpen(false)}
                            data-testid="cost-tracker-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            className="bg-[#2EB4AC] hover:bg-[#259993] text-white"
                            data-testid="cost-tracker-save"
                        >
                            {saving ? "Saving…" : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default CostTrackerWidget;
